/**
 * Workflow suite panel — embeddable content for the benchmark page.
 * Contains KPIs, sub-tabs (Trends / Suite Runs), and detail views.
 *
 * @module pages/WorkflowSuitePanel
 * @see docs/api/services/benchmark.md
 */
import React from 'react';
import { Box, KPICard, Divider, UnderlineTabs, UnderlineTab } from '@chatsdet/ui';
import { Timer, CurrencyCircleDollar, CheckCircle, ListChecks } from '@phosphor-icons/react';
import { useWorkflowSuiteTrendsQuery } from '../useBenchmarkData';
import {
  computeKPIs,
  filterTrendsForSelection,
  formatDuration,
  formatTokenCount,
} from './useWorkflowSuiteTransforms';
import { WorkflowSuiteTrendCharts } from './WorkflowSuiteTrendCharts';
import { WorkflowSuiteRunsTable } from './WorkflowSuiteRunsTable';
import { WorkflowSuiteDetail } from './WorkflowSuiteDetail';
import { parseRunNumbers, useWorkflowSuiteSearch } from './useWorkflowSuiteSearch';

export function WorkflowSuitePanel() {
  const { search, updateSearch } = useWorkflowSuiteSearch();

  // Sub-tab within workflow suites (trends vs suite runs)
  // Use a derived value: if suiteId is set we show detail, otherwise use subtab
  const subtab = search.tab === 'suites' ? 'suites' : 'trends';
  const days = search.days ?? 30;
  const triggerFilter = search.trigger ?? 'all';
  const configFilter = search.configId ?? 'all';
  const selectedRuns = React.useMemo(() => parseRunNumbers(search.runs), [search.runs]);
  const selectedWorkflows = React.useMemo(() => search.workflows ?? [], [search.workflows]);
  const workflowFilterNames = React.useMemo(
    () => (selectedWorkflows.length === 0 ? undefined : selectedWorkflows),
    [selectedWorkflows]
  );
  const { suiteId } = search;

  const trendsQuery = useWorkflowSuiteTrendsQuery({
    days,
    configId: configFilter === 'all' ? undefined : configFilter,
  });

  const kpiTrends = React.useMemo(() => {
    const triggerFiltered =
      triggerFilter === 'all'
        ? (trendsQuery.data?.trends ?? [])
        : (trendsQuery.data?.trends ?? []).filter((t) => t.triggerType === triggerFilter);
    return filterTrendsForSelection(triggerFiltered, workflowFilterNames, selectedRuns);
  }, [trendsQuery.data, triggerFilter, workflowFilterNames, selectedRuns]);

  const kpis = React.useMemo(() => computeKPIs(kpiTrends), [kpiTrends]);

  // If a suite is selected, show its detail view
  if (suiteId) {
    return (
      <Box className="space-y-6">
        <WorkflowSuiteDetail
          suiteId={suiteId}
          onBack={() => updateSearch({ suiteId: undefined })}
        />
      </Box>
    );
  }

  return (
    <Box className="space-y-6">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          icon={<ListChecks size={18} />}
          title="Total Suites"
          value={kpis.totalSuites}
          subtitle={`Last ${days} days`}
        />
        <KPICard
          icon={<CheckCircle size={18} />}
          title="Success Rate"
          value={Math.round(kpis.successRate * 100)}
          subtitle="%"
          variant={
            kpis.successRate >= 0.8
              ? 'success'
              : kpis.successRate >= 0.5
                ? 'warning'
                : 'destructive'
          }
        />
        <KPICard
          icon={<Timer size={18} />}
          title="Avg Duration"
          formattedValue={formatDuration(kpis.avgDurationSec * 1000)}
        />
        <KPICard
          icon={<CurrencyCircleDollar size={18} />}
          title="Avg Tokens"
          formattedValue={formatTokenCount(kpis.avgTokens)}
        />
      </div>

      <Divider size="lg" />

      {/* Sub-tabs */}
      <UnderlineTabs
        selectedId={subtab}
        onSelectionChange={(id) => updateSearch({ tab: String(id) })}
        layoutId="workflow-suite-tabs"
        className="text-base"
      >
        <UnderlineTab id="trends">Trends</UnderlineTab>
        <UnderlineTab id="suites">Suite Runs</UnderlineTab>
      </UnderlineTabs>

      {/* Sub-tab content */}
      {subtab === 'trends' ? (
        <WorkflowSuiteTrendCharts />
      ) : (
        <WorkflowSuiteRunsTable onSelectSuite={(id) => updateSearch({ suiteId: id })} />
      )}
    </Box>
  );
}
